import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import "./ReportsSectionCompact.css";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import {
  getDailyClosingPrefill,
  getDailyClosingReport,
  getDailyClosingReportByDate,
  listDailyClosingReports,
  upsertDailyClosingReport,
  uploadDailyClosingReceipt,
} from "../api/reports";
import { getCashDrawer } from "../api/cashDrawer";
import { useAuth } from "../context/AuthContext";
import { toISODateString } from "../utils/dateOnly";
import { getSalonTimezone } from "../utils/salonTimezone";
import { DAILY_CLOSING_CASH_TOOLTIPS } from "../constants/reportTooltips";
import { REPORT_HELP } from "../constants/reportHelpContent";
import {
  getReportPathForRoute,
  getReportsHomePathForRoute,
} from "../utils/reportRouteContext";

const EXPENSE_CATEGORIES = [
  "Payroll",
  "Beauty or Nail Supplies",
  "Beauty or Nail Equipment",
  "Cleaning & Laundry Supplies",
  "Repairs",
  "Miscellaneous",
];

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const parts = formatter.formatToParts(date);
      const lookup = { year: "", month: "", day: "" };
      for (const part of parts) {
        if (Object.prototype.hasOwnProperty.call(lookup, part.type)) {
          lookup[part.type] = part.value;
        }
      }
      if (lookup.year && lookup.month && lookup.day) {
        return `${lookup.year}-${lookup.month}-${lookup.day}`;
      }
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateFromParts = (year, month, day) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const formatMoney = (value) => {
  const number = Number(value ?? 0);
  if (Number.isNaN(number)) return "$0.00";
  return `$${number.toFixed(2)}`;
};

const parseNumber = (value) => {
  const number = Number(value);
  return Number.isNaN(number) ? 0 : number;
};

const getAmountInputState = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return { hasInput: false, isValid: true, value: 0 };
  }
  const parsed = Number(raw);
  return { hasInput: true, isValid: Number.isFinite(parsed), value: parsed };
};

const isExpenseRowActive = (row) => {
  const category = String(row.category || "").trim();
  const description = String(row.description || "").trim();
  const { hasInput, isValid, value } = getAmountInputState(row.amount);
  const hasAmount = hasInput && (!isValid || value !== 0);
  const hasReceipt = Boolean(row.receipt_url) || Boolean(row.receipt_selected);
  return Boolean(category || description || hasAmount || hasReceipt);
};

const newExpenseRow = () => ({
  id: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  amount: "",
  category: "",
  description: "",
  receipt_url: "",
  receipt_selected: false,
  uploading: false,
});

export default function DailyClosingReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const lastLoadedWorkDateRef = useRef("");
  const loadRequestIdRef = useRef(0);
  const didApplyDefaultWorkDateRef = useRef(false);
  const { currentSalon, user } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.DAILY_CLOSING;
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );
  const today = useMemo(
    () => formatDateISO(new Date(), salonTimezone),
    [salonTimezone]
  );
  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate(getReportsHomePathForRoute(location.pathname), { replace: true });
  };
  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={DAILY_CLOSING_CASH_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );
  const normalizedToday = useMemo(() => toISODateString(today), [today]);
  const workDateFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("work_date") || "";
  }, [location.search]);
  const normalizedQueryWorkDateISO = useMemo(
    () => toISODateString(workDateFromQuery),
    [workDateFromQuery]
  );
  const incomingISO =
    typeof location.state?.preselectedWorkDateISO === "string"
      ? location.state.preselectedWorkDateISO
      : "";
  const requestedWorkDateISO = useMemo(() => {
    if (normalizedQueryWorkDateISO) return normalizedQueryWorkDateISO;
    const normalizedIncoming = toISODateString(incomingISO);
    if (normalizedIncoming) return normalizedIncoming;
    return "";
  }, [incomingISO, normalizedQueryWorkDateISO]);
  const initialWorkDate = requestedWorkDateISO || normalizedToday || "";
  const [workDate, setWorkDate] = useState(() => initialWorkDate);
  const [prefillLoaded, setPrefillLoaded] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillError, setPrefillError] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [expenseErrors, setExpenseErrors] = useState({});
  const historyDefaults = useMemo(() => {
    if (!today) {
      return { start: "", end: "" };
    }
    const year = Number(today.slice(0, 4));
    const month = Number(today.slice(5, 7));
    return {
      start: formatDateFromParts(year, month, 1),
      end: today,
    };
  }, [today]);
  const [historyStart, setHistoryStart] = useState(historyDefaults.start);
  const [historyEnd, setHistoryEnd] = useState(historyDefaults.end);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [noSavedReportMessage, setNoSavedReportMessage] = useState("");

  const [prefill, setPrefill] = useState({
    paid_by_gift_card_total: "0.00",
    tips_received_total: "0.00",
    tips_received_non_exempt: "0.00",
    tips_received_exempt: "0.00",
    gift_cards_sold_total: "0.00",
  });
  const [posBankCardTotalIncludingTips, setPosBankCardTotalIncludingTips] =
    useState("0");
  const [posCashOnHandMinusBeginningCash, setPosCashOnHandMinusBeginningCash] =
    useState("0");
  const [totalSheet, setTotalSheet] = useState({
    grand_total: "0.00",
    tips_total: "0.00",
  });
  const [cashTipWithdrawals, setCashTipWithdrawals] = useState("0");
  const [cashBonusWithdrawals, setCashBonusWithdrawals] = useState("0");
  const [notes, setNotes] = useState("");
  const [expenses, setExpenses] = useState([]);

  const posBankCardTotalValue = parseNumber(posBankCardTotalIncludingTips);
  const posCashOnHandValue = parseNumber(posCashOnHandMinusBeginningCash);
  const paidByGiftCardTotal = parseNumber(prefill.paid_by_gift_card_total);
  const cashTipWithdrawalsValue = parseNumber(cashTipWithdrawals);
  const cashBonusWithdrawalsValue = parseNumber(cashBonusWithdrawals);

  const cashNet =
    posCashOnHandValue - cashTipWithdrawalsValue - cashBonusWithdrawalsValue;
  const totalSales =
    posBankCardTotalValue +
    paidByGiftCardTotal +
    posCashOnHandValue -
    cashTipWithdrawalsValue -
    cashBonusWithdrawalsValue;
  const cashExpensesTotal = expenses.reduce(
    (sum, row) => sum + parseNumber(row.amount),
    0
  );
  const cashOutTotal =
    cashTipWithdrawalsValue + cashBonusWithdrawalsValue + cashExpensesTotal;
  const expectedCashRemaining =
    posCashOnHandValue -
    cashTipWithdrawalsValue -
    cashBonusWithdrawalsValue -
    cashExpensesTotal;

  const totalSheetGrandTotal = parseNumber(totalSheet.grand_total);
  const rawDifference = totalSales - totalSheetGrandTotal;
  const difference = Math.round(rawDifference * 100) / 100;
  const isReconciled = Math.abs(difference) < 0.005;

  const resetFormForDate = (nextDate) => {
    setWorkDate(nextDate);
    setPrefillLoaded(false);
    setPrefillError("");
    setSaveError("");
    setSaveSuccess("");
    setNoSavedReportMessage("");
    setPrefill({
      paid_by_gift_card_total: "0.00",
      tips_received_total: "0.00",
      tips_received_non_exempt: "0.00",
      tips_received_exempt: "0.00",
      gift_cards_sold_total: "0.00",
    });
    setPosBankCardTotalIncludingTips("0");
    setPosCashOnHandMinusBeginningCash("0");
    setTotalSheet({ grand_total: "0.00", tips_total: "0.00" });
    setCashTipWithdrawals("0");
    setCashBonusWithdrawals("0");
    setNotes("");
    setExpenses([]);
    setExpenseErrors({});
  };

  const syncWorkDateInUrl = (nextDate) => {
    const normalizedDate = toISODateString(nextDate);
    if (!normalizedDate) return;
    const dailyClosingPath = getReportPathForRoute(
      location.pathname,
      "/manager/reports/daily-closing"
    );
    navigate(`${dailyClosingPath}?work_date=${normalizedDate}`, {
      replace: true,
      state: {
        ...location.state,
        preselectedWorkDateISO: normalizedDate,
      },
    });
  };

  const handleWorkDateChange = (event) => {
    const normalizedDate = toISODateString(event.target.value);
    if (!normalizedDate) return;
    resetFormForDate(normalizedDate);
    syncWorkDateInUrl(normalizedDate);
  };

  const applyReportToForm = (data) => {
    if (!data) return;
    const normalizedDate = toISODateString(data.work_date);
    const resolvedPosBankCard = (() => {
      const posValue = parseNumber(data.pos_bank_card_total_including_tips);
      if (posValue > 0) return String(data.pos_bank_card_total_including_tips);
      const legacyValue = parseNumber(data.bank_card_total);
      if (legacyValue > 0) return String(data.bank_card_total);
      return String(data.pos_bank_card_total_including_tips ?? "0");
    })();
    const resolvedPosCashOnHand = (() => {
      const posValue = parseNumber(data.pos_cash_on_hand_minus_beginning_cash);
      if (posValue > 0)
        return String(data.pos_cash_on_hand_minus_beginning_cash);
      const legacyValue = parseNumber(data.cash_tender_gross);
      if (legacyValue > 0) return String(data.cash_tender_gross);
      return String(data.pos_cash_on_hand_minus_beginning_cash ?? "0");
    })();
    setWorkDate(normalizedDate || workDate);
    setPrefillLoaded(true);
    setSaveError("");
    const tipsTotalFallback = data.tips_received_total ?? "0.00";
    setPrefill({
      paid_by_gift_card_total: data.paid_by_gift_card_total ?? "0.00",
      tips_received_total: tipsTotalFallback,
      tips_received_non_exempt:
        data.tips_received_non_exempt ?? tipsTotalFallback,
      tips_received_exempt: data.tips_received_exempt ?? "0.00",
      gift_cards_sold_total: data.gift_cards_sold_total ?? "0.00",
    });
    setPosBankCardTotalIncludingTips(resolvedPosBankCard);
    setPosCashOnHandMinusBeginningCash(resolvedPosCashOnHand);
    setTotalSheet({
      grand_total: data.total_sheet_grand_total ?? "0.00",
      tips_total: data.total_sheet_tips_total ?? "0.00",
    });
    setCashTipWithdrawals(String(data.cash_tip_withdrawals ?? "0"));
    setCashBonusWithdrawals(String(data.cash_bonus_withdrawals ?? "0"));
    setNotes(data.notes || "");
    const incomingExpenses = (data.expenses || []).map((expense) => ({
      id: `saved-${expense.id}`,
      amount: expense.amount ?? "0",
      category: expense.category || "",
      description: expense.description || "",
      receipt_url: expense.receipt_url || "",
      receipt_selected: Boolean(expense.receipt_url),
      uploading: false,
    }));
    setExpenses(incomingExpenses.length ? incomingExpenses : []);
    setExpenseErrors({});
  };

  const handlePrefill = async () => {
    if (!workDate) return;
    setPrefillLoading(true);
    setPrefillError("");
    setSaveSuccess("");
    setNoSavedReportMessage("");
    try {
      const cashDrawerPromise = getCashDrawer({
        date: workDate,
        salon_id: user?.salon_id,
      }).catch(() => null);
      const [data, drawer] = await Promise.all([
        getDailyClosingPrefill({ date: workDate }),
        cashDrawerPromise,
      ]);
      const tipsTotalFallback = data?.prefill?.tips_received_total ?? "0.00";
      setPrefill({
        paid_by_gift_card_total: data?.prefill?.paid_by_gift_card_total ?? "0.00",
        tips_received_total: tipsTotalFallback,
        tips_received_non_exempt:
          data?.prefill?.tips_received_non_exempt ?? tipsTotalFallback,
        tips_received_exempt: data?.prefill?.tips_received_exempt ?? "0.00",
        gift_cards_sold_total: data?.prefill?.gift_cards_sold_total ?? "0.00",
      });
      setTotalSheet({
        grand_total: data?.total_sheet?.grand_total ?? "0.00",
        tips_total: data?.total_sheet?.tips_total ?? "0.00",
      });
      if (
        drawer &&
        !(prefillLoaded && parseNumber(posCashOnHandMinusBeginningCash) > 0)
      ) {
        const cashOnHand =
          Number(drawer.in_drawer_amount ?? 0) -
          Number(drawer.begin_amount ?? 0);
        const clampedCashOnHand = Math.max(0, cashOnHand);
        setPosCashOnHandMinusBeginningCash(clampedCashOnHand.toFixed(2));
      }
      setPrefillLoaded(true);
    } catch (error) {
      setPrefillError("Unable to load Total Sheet reference totals.");
      setPrefillLoaded(false);
    } finally {
      setPrefillLoading(false);
    }
  };

  const handleSave = async () => {
    if (!workDate) return;
    setSaveLoading(true);
    setSaveError("");
    setSaveSuccess("");
    const validationErrors = {};
    let hasValidationError = false;
    const activeExpenses = expenses.filter((row) => isExpenseRowActive(row));
    activeExpenses.forEach((row) => {
      const category = String(row.category || "").trim();
      const { hasInput, isValid, value } = getAmountInputState(row.amount);
      const rowErrors = {};
      if (!category) {
        rowErrors.category = "Choose an expense category.";
      }
      if (!hasInput || !isValid || value <= 0) {
        rowErrors.amount = "Enter an amount greater than 0.";
      }
      if (Object.keys(rowErrors).length) {
        validationErrors[row.id] = rowErrors;
        hasValidationError = true;
      }
    });
    if (hasValidationError) {
      setExpenseErrors(validationErrors);
      setSaveError("Please fix the highlighted cash expense fields.");
      setSaveLoading(false);
      return;
    }
    setExpenseErrors({});
    const cleanedExpenses = activeExpenses.map((row) => ({
      amount: getAmountInputState(row.amount).value,
      category: String(row.category || "").trim(),
      description: row.description || null,
      receipt_url: row.receipt_url || null,
    }));
    try {
      const payload = {
        work_date: workDate,
        pos_bank_card_total_including_tips: parseNumber(
          posBankCardTotalIncludingTips
        ),
        pos_cash_on_hand_minus_beginning_cash: parseNumber(
          posCashOnHandMinusBeginningCash
        ),
        cash_tip_withdrawals: parseNumber(cashTipWithdrawals),
        cash_bonus_withdrawals: parseNumber(cashBonusWithdrawals),
        notes: notes || null,
        expenses: cleanedExpenses,
      };
      const data = await upsertDailyClosingReport(payload);
      applyReportToForm(data);
      setSaveSuccess("Daily closing report saved.");
      await loadHistory();
    } catch (error) {
      setSaveError("Unable to save daily closing report.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleExpenseChange = (index, field, value) => {
    setExpenses((prev) =>
      prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row))
    );
    setExpenseErrors({});
    setSaveError("");
  };

  const handleAddExpense = () => {
    setExpenses((prev) => [...prev, newExpenseRow()]);
    setExpenseErrors({});
    setSaveError("");
  };

  const handleRemoveExpense = (index) => {
    setExpenses((prev) => prev.filter((_, idx) => idx !== index));
    setExpenseErrors({});
    setSaveError("");
  };

  const handleReceiptUpload = async (index, file) => {
    if (!file) return;
    setExpenses((prev) =>
      prev.map((row, idx) =>
        idx === index
          ? { ...row, uploading: true, receipt_selected: true }
          : row
      )
    );
    try {
      const data = await uploadDailyClosingReceipt(file);
      setExpenses((prev) =>
        prev.map((row, idx) =>
          idx === index
            ? {
                ...row,
                receipt_url: data.receipt_url,
                receipt_selected: true,
                uploading: false,
              }
            : row
        )
      );
    } catch (error) {
      setSaveError("Receipt upload failed. Please try again.");
      setExpenses((prev) =>
        prev.map((row, idx) =>
          idx === index
            ? { ...row, receipt_selected: true, uploading: false }
            : row
        )
      );
    }
  };

  const loadHistory = async () => {
    if (!historyStart || !historyEnd) return;
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const data = await listDailyClosingReports({
        start: historyStart,
        end: historyEnd,
      });
      setHistoryItems(data.items || []);
    } catch (error) {
      setHistoryError("Unable to load history.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleLoadReport = async (reportId) => {
    if (!reportId) return;
    setDetailLoading(true);
    setSaveSuccess("");
    setSaveError("");
    setNoSavedReportMessage("");
    try {
      const data = await getDailyClosingReport(reportId);
      applyReportToForm(data);
      if (data?.work_date) {
        syncWorkDateInUrl(data.work_date);
      }
    } catch (error) {
      setSaveError("Unable to load saved report.");
    } finally {
      setDetailLoading(false);
    }
  };

  const loadSavedReportForWorkDate = async (dateValue) => {
    const normalizedDate = toISODateString(dateValue);
    if (!normalizedDate) return;
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setDetailLoading(true);
    setSaveSuccess("");
    setSaveError("");
    setNoSavedReportMessage("");
    try {
      const reportData = await getDailyClosingReportByDate({
        work_date: normalizedDate,
      });
      if (loadRequestIdRef.current !== requestId) return;
      if (reportData) {
        applyReportToForm(reportData);
      } else {
        setNoSavedReportMessage(
          "No saved daily closing report found for this date. You can enter values manually or load from tickets."
        );
      }
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (
        status === 404 &&
        detail === "DAILY_CLOSING_REPORT_NOT_FOUND"
      ) {
        if (loadRequestIdRef.current !== requestId) return;
        setNoSavedReportMessage(
          "No saved daily closing report found for this date. You can enter values manually or load from tickets."
        );
        return;
      }
      if (loadRequestIdRef.current !== requestId) return;
      setSaveError("Unable to load saved report.");
    } finally {
      if (loadRequestIdRef.current === requestId) {
        setDetailLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!normalizedQueryWorkDateISO) return;
    if (workDateFromQuery === normalizedQueryWorkDateISO) return;
    navigate(
      `${getReportPathForRoute(
        location.pathname,
        "/manager/reports/daily-closing"
      )}?work_date=${normalizedQueryWorkDateISO}`,
      {
        replace: true,
        state: {
          ...location.state,
          preselectedWorkDateISO: normalizedQueryWorkDateISO,
        },
      }
    );
  }, [
    location.pathname,
    location.state,
    navigate,
    normalizedQueryWorkDateISO,
    workDateFromQuery,
  ]);

  useEffect(() => {
    if (requestedWorkDateISO) {
      if (requestedWorkDateISO !== workDate) {
        lastLoadedWorkDateRef.current = "";
        resetFormForDate(requestedWorkDateISO);
      }
      didApplyDefaultWorkDateRef.current = true;
      return;
    }
    if (!currentSalon) return;
    if (!normalizedToday) return;
    if (!didApplyDefaultWorkDateRef.current) {
      if (normalizedToday !== workDate) {
        lastLoadedWorkDateRef.current = "";
        resetFormForDate(normalizedToday);
      }
      didApplyDefaultWorkDateRef.current = true;
    }
  }, [currentSalon, normalizedToday, requestedWorkDateISO, workDate]);

  useEffect(() => {
    if (!workDate) return;
    if (lastLoadedWorkDateRef.current === workDate) return;
    lastLoadedWorkDateRef.current = workDate;
    loadSavedReportForWorkDate(workDate);
  }, [workDate]);

  useEffect(() => {
    loadHistory();
  }, [historyStart, historyEnd]);

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Daily Closing Cash Report
            </div>
            <div className="flex items-center gap-3">
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div id="daily-closing-overview" className={getSectionClassName("daily-closing-overview")}>
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id="daily-closing-filters"
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-5 ${getSectionClassName(
                "daily-closing-filters"
              )}`}
            >
              <div className="grid gap-4 md:grid-cols-[1fr_auto_auto] items-end">
                    <div className="flex flex-col gap-1">
                      {renderFieldLabel("Work Date", "work_date")}
                      <input
                        type="date"
                        value={workDate}
                        onChange={handleWorkDateChange}
                        className={FIELD_CLASS}
                  />
                </div>
                <button
                  type="button"
                  onClick={handlePrefill}
                  className={BTN_OUTLINE}
                  disabled={prefillLoading}
                >
                  {prefillLoading ? "Loading..." : "Load from tickets"}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className={BTN_PRIMARY}
                  disabled={saveLoading}
                >
                  {saveLoading ? "Saving..." : "Save / Submit"}
                </button>
              </div>
              {!prefillLoaded && (
                <div className="mt-3 text-sm md:text-base text-slate-600">
                  Load from tickets to prefill totals.
                </div>
              )}
            </div>

            {prefillError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {prefillError}
              </div>
            )}

            {noSavedReportMessage && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-700 text-sm md:text-base">
                {noSavedReportMessage}
              </div>
            )}

            {saveError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {saveError}
              </div>
            )}

            {saveSuccess && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 text-sm md:text-base">
                {saveSuccess}
              </div>
            )}

            <div
              id="daily-closing-details"
              className={`grid gap-6 lg:grid-cols-[2fr_1fr] ${getSectionClassName(
                "daily-closing-details"
              )}`}
            >
              <div className="space-y-6">
                <div
                  id="daily-closing-kpis"
                  className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${getSectionClassName(
                    "daily-closing-kpis"
                  )}`}
                >
                  <div className="text-lg md:text-xl font-semibold text-slate-900">
                    Sales Revenue
                  </div>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>Total Bank Card (Including Tips)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={posBankCardTotalIncludingTips}
                        onChange={(event) =>
                          setPosBankCardTotalIncludingTips(event.target.value)
                        }
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>Total Cash on Hand (Minus Beginning Cash)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={posCashOnHandMinusBeginningCash}
                        onChange={(event) =>
                          setPosCashOnHandMinusBeginningCash(event.target.value)
                        }
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>
                        Tips Received (Non-Exempt Tickets)
                      </label>
                      <input
                        type="text"
                        value={prefill.tips_received_non_exempt}
                        className={FIELD_CLASS}
                        disabled
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>
                        Tips Received (Tax-Exempt Tickets)
                      </label>
                      <input
                        type="text"
                        value={prefill.tips_received_exempt}
                        className={FIELD_CLASS}
                        disabled
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>Gift Cards Sold Total</label>
                      <input
                        type="text"
                        value={prefill.gift_cards_sold_total}
                        className={FIELD_CLASS}
                        disabled
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>Paid by Gift Card Total</label>
                      <input
                        type="text"
                        value={prefill.paid_by_gift_card_total}
                        className={FIELD_CLASS}
                        disabled
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-lg md:text-xl font-semibold text-slate-900">
                    Cash Withdrawals
                  </div>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>
                        Cash Tip Withdrawals to Technicians
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={cashTipWithdrawals}
                        onChange={(event) =>
                          setCashTipWithdrawals(event.target.value)
                        }
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>
                        Cash Bonus Withdrawals to Technicians
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={cashBonusWithdrawals}
                        onChange={(event) =>
                          setCashBonusWithdrawals(event.target.value)
                        }
                        className={FIELD_CLASS}
                      />
                    </div>
                  </div>
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Cash Total (Minus Tip &amp; Bonus)
                    </div>
                    <div className="text-lg font-semibold text-slate-900">
                      {formatMoney(cashNet)}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-lg md:text-xl font-semibold text-slate-900">
                      Cash Expenses
                    </div>
                    <button
                      type="button"
                      onClick={handleAddExpense}
                      className={BTN_OUTLINE}
                    >
                      Add expense
                    </button>
                  </div>
                  <div className="mt-4 space-y-4">
                    {expenses.map((row, index) => (
                      <div
                        key={row.id}
                        className="rounded-2xl border border-slate-200 p-4"
                      >
                        <div className="grid gap-4 md:grid-cols-[1.2fr_0.6fr]">
                          <div className="flex flex-col gap-1">
                            <label className={LABEL_TEXT}>Category</label>
                            <select
                              value={row.category}
                              onChange={(event) =>
                                handleExpenseChange(
                                  index,
                                  "category",
                                  event.target.value
                                )
                              }
                              className={FIELD_CLASS}
                            >
                              <option value="">Choose an expense category</option>
                              {EXPENSE_CATEGORIES.map((category) => (
                                <option key={category} value={category}>
                                  {category}
                                </option>
                              ))}
                            </select>
                            {expenseErrors[row.id]?.category && (
                              <div className="text-sm text-rose-600">
                                {expenseErrors[row.id].category}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className={LABEL_TEXT}>Amount (Cash)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={row.amount}
                              onChange={(event) =>
                                handleExpenseChange(
                                  index,
                                  "amount",
                                  event.target.value
                                )
                              }
                              className={FIELD_CLASS}
                            />
                            {expenseErrors[row.id]?.amount && (
                              <div className="text-sm text-rose-600">
                                {expenseErrors[row.id].amount}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 grid gap-4 md:grid-cols-[1.4fr_0.6fr] items-end">
                          <div className="flex flex-col gap-1">
                            <label className={LABEL_TEXT}>Description</label>
                            <input
                              type="text"
                              value={row.description}
                              onChange={(event) =>
                                handleExpenseChange(
                                  index,
                                  "description",
                                  event.target.value
                                )
                              }
                              className={FIELD_CLASS}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className={LABEL_TEXT}>Receipt</label>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(event) =>
                                handleReceiptUpload(index, event.target.files?.[0])
                              }
                              className="text-sm md:text-base"
                            />
                            {row.receipt_url && (
                              <div className="text-sm text-slate-600 break-all">
                                {row.receipt_url}
                              </div>
                            )}
                            {row.uploading && (
                              <div className="text-sm text-slate-500">
                                Uploading...
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                          <button
                            type="button"
                            onClick={() => handleRemoveExpense(index)}
                            className="text-sm md:text-base font-semibold text-rose-600 hover:text-rose-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Total Cash Expenses
                    </div>
                    <div className="text-lg font-semibold text-slate-900">
                      {formatMoney(cashExpensesTotal)}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-lg md:text-xl font-semibold text-slate-900">
                    Notes
                  </div>
                  <textarea
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    rows={4}
                    className="mt-3 w-full rounded-xl border border-slate-300 p-3 text-base md:text-lg focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-lg md:text-xl font-semibold text-slate-900">
                    Totals &amp; Reconciliation
                  </div>
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">
                        Total Sales (Daily Closing)
                      </span>
                      <span className="font-semibold text-slate-900">
                        {formatMoney(totalSales)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">
                        Total Sheet Grand Total
                      </span>
                      <span className="font-semibold text-slate-900">
                        {formatMoney(totalSheetGrandTotal)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Difference</span>
                      <span
                        className={`font-semibold ${isReconciled ? "text-emerald-600" : "text-rose-600"
                          }`}
                      >
                        {formatMoney(difference)}
                      </span>
                    </div>
                  </div>
                  {!isReconciled && (
                    <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                      Total Sales does not match Total Sheet for this date.
                    </div>
                  )}
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Expected Cash Remaining
                    </div>
                    <div className="text-lg font-semibold text-slate-900">
                      {formatMoney(expectedCashRemaining)}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-lg md:text-xl font-semibold text-slate-900">
                    History
                  </div>
                  <div className="mt-4 grid gap-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="flex flex-col gap-1">
                        {renderFieldLabel("Start", "history_start")}
                        <input
                          type="date"
                          value={historyStart}
                          onChange={(event) =>
                            setHistoryStart(event.target.value)
                          }
                          className={FIELD_CLASS}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        {renderFieldLabel("End", "history_end")}
                        <input
                          type="date"
                          value={historyEnd}
                          onChange={(event) =>
                            setHistoryEnd(event.target.value)
                          }
                          className={FIELD_CLASS}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={loadHistory}
                      className={BTN_OUTLINE}
                      disabled={historyLoading}
                    >
                      {historyLoading ? "Loading..." : "Refresh history"}
                    </button>
                  </div>
                  {historyError && (
                    <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                      {historyError}
                    </div>
                  )}
                  <div className="mt-4 space-y-3">
                    {historyItems.length === 0 ? (
                      <div className="text-sm md:text-base text-slate-500">
                        No daily closing reports found for this range.
                      </div>
                    ) : (
                      historyItems.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleLoadReport(item.id)}
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-left hover:border-rose-200"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-slate-900">
                                {item.work_date}
                              </div>
                              <div className="text-sm text-slate-600">
                                Total Sales: {formatMoney(item.total_sales)}
                              </div>
                            </div>
                            <div className="text-sm text-slate-500">
                              {item.updated_at ? "Updated" : "Saved"}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                    {detailLoading && (
                      <div className="text-sm md:text-base text-slate-500">
                        Loading report...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div
              id="daily-closing-export"
              className={`rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 ${getSectionClassName(
                "daily-closing-export"
              )}`}
            >
              Export is not available on this report.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
